/**
 * StageCoda — the frame after the last look (plan 2026_09_23_pack_stage_coda.md § Decided 1, board
 * `design/toile_wall/TenthFrame.dc.html` frame B). The wall's ghost at stage size — dashed accent
 * border, the pen — over one of the pack's pictures faded behind it (the house passes the last look,
 * the one the visitor just left). It holds the house's action as children.
 *
 * No pager zones render over it: the coda spends its own clicks (field, chips, verb), the same
 * safety property narcis `pagerLooks.ts` states for its `canvas`/`preview`/`gate` stages. The
 * house's swipe and key handlers still page from it — they sit on the frame, not in the zones.
 *
 * The frame takes `ratio` as its MINIMUM shape and grows to fit the action: a wide pack's frame is
 * shorter than a field, chips and a verb, and clipping the verb would be worse than a taller frame.
 *
 * The sub-line folds away while anything inside has focus (`:focus-within`, no JS), so on a phone
 * the field, chips and verb sit above the keyboard.
 *
 * Colours come from the house's `--stage-*` variables only; every string is a prop.
 */
import type {ReactNode} from 'react'

import {Pen} from '@core/wall/ui/Ghost'
import styles from './StageCoda.module.scss'

export interface StageCodaProps {
    /** The picture faded behind the frame. */
    backdrop: string
    /** The frame's minimum shape — the pack's own ratio. */
    ratio: {w: number; h: number}
    /** The small first line of the head ("Make your own"). */
    lead: string
    /** The large second line — the pack's own name, on its own line so no casing rule is needed. */
    head: string
    /** The line under the head; folds while the frame holds focus. */
    sub: string
    children: ReactNode
}

export function StageCoda({backdrop, ratio, lead, head, sub, children}: StageCodaProps) {
    return (
        <div className={styles.coda} style={{aspectRatio: `${ratio.w} / ${ratio.h}`}}>
            <img className={styles.backdrop} src={backdrop} alt="" aria-hidden="true" loading="lazy" decoding="async" draggable={false} />
            <div className={styles.body}>
                <span className={styles.pen} aria-hidden="true"><Pen /></span>
                <p className={styles.head}>
                    <span className={styles.lead}>{lead}</span>
                    <span className={styles.name}>{head}</span>
                </p>
                <p className={styles.sub}>{sub}</p>
                <div className={styles.action}>{children}</div>
            </div>
        </div>
    )
}
